import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import "./Header.css";
import HeaderLogo from "./HeaderLogo";
import ContactModal from "./ContactModal";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navVisible, setNavVisible] = useState(true);
  const [contactPos, setContactPos] = useState({ x: 0, y: 0 });
  const [contactProximity, setContactProximity] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [navClickedRecently, setNavClickedRecently] = useState(false);
  const [smoothScrolling, setSmoothScrolling] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [userHasScrolledManually, setUserHasScrolledManually] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const contactRef = useRef(null);

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  // Function to handle navigation to home page sections
  const handleSectionNavigation = (sectionId) => {
    setNavVisible(false); // Hide nav immediately
    setNavClickedRecently(true); // Set flag to prevent auto-show
    setUserHasScrolledManually(false); // Reset manual scroll flag
    setSmoothScrolling(true); // Mark that we're smooth scrolling

    if (isHomePage) {
      // If already on home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        // Reset smooth scrolling flag after animation
        setTimeout(() => {
          setSmoothScrolling(false);
        }, 1200);
      }
    } else {
      // If on different page, navigate to home first, then scroll
      navigate("/");
      // Wait a bit for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          // Reset smooth scrolling flag after animation
          setTimeout(() => {
            setSmoothScrolling(false);
          }, 1200);
        }
      }, 100);
    }
  };

  // Listen for card hover events
  useEffect(() => {
    const handleCardHoverStart = () => setCardHovered(true);
    const handleCardHoverEnd = () => setCardHovered(false);

    window.addEventListener("cardHoverStart", handleCardHoverStart);
    window.addEventListener("cardHoverEnd", handleCardHoverEnd);

    return () => {
      window.removeEventListener("cardHoverStart", handleCardHoverStart);
      window.removeEventListener("cardHoverEnd", handleCardHoverEnd);
    };
  }, []);

  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const isAtTop = scrollY < 100; // Consider top 100px as "upper part of page"

      // If we're at the top of the page, always show navigation
      if (isAtTop) {
        setNavVisible(true);
        return;
      }

      // For the rest of the page, use the existing logic
      setNavVisible(false);

      // Mark that user has scrolled manually (not programmatically)
      if (!smoothScrolling) {
        setUserHasScrolledManually(true);
        setNavClickedRecently(false); // Reset navbar click flag on manual scroll
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Only show navbar if user has scrolled manually after navbar click
        if (!cardHovered && (!navClickedRecently || userHasScrolledManually)) {
          setNavVisible(true);
        }
      }, 1500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [
    navClickedRecently,
    smoothScrolling,
    cardHovered,
    userHasScrolledManually,
  ]);

  useEffect(() => {
    let animationId;
    let lastMoveTime = 0;

    const handleMouseMove = (e) => {
      if (!contactRef.current) return;

      const now = Date.now();
      if (now - lastMoveTime < 16) return; // Throttle to ~60fps
      lastMoveTime = now;

      const btn = contactRef.current;
      const rect = btn.getBoundingClientRect();
      const btnCenterX = rect.left + rect.width / 2;
      const btnCenterY = rect.top + rect.height / 2;

      const deltaX = e.clientX - btnCenterX;
      const deltaY = e.clientY - btnCenterY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Magnetic area radius
      const magneticRadius = 120;

      if (distance < magneticRadius) {
        const strength = (magneticRadius - distance) / magneticRadius;
        const targetX = deltaX * strength * 0.15;
        const targetY = deltaY * strength * 0.15;

        // Set proximity for green glow effect
        setContactProximity(strength);

        // Smooth interpolation with threshold
        const threshold = 0.5;
        const currentX = contactPos.x;
        const currentY = contactPos.y;

        const diffX = Math.abs(targetX - currentX);
        const diffY = Math.abs(targetY - currentY);

        if (diffX > threshold || diffY > threshold) {
          const smoothX = currentX + (targetX - currentX) * 0.3;
          const smoothY = currentY + (targetY - currentY) * 0.3;
          setContactPos({ x: smoothX, y: smoothY });
        }
      } else {
        setContactPos({ x: 0, y: 0 });
        setContactProximity(0);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [contactPos]);

  return (
    <header className="header">
      <HeaderLogo />
      <nav
        className={`header-nav${navVisible ? "" : " header-nav-hidden"}`}
        onClick={(e) => {
          const a = e.target.closest('a[href^="#"]');
          if (a) {
            const hash = a.getAttribute("href");
            if (hash && hash.startsWith("#")) {
              e.preventDefault();
              setNavVisible(false); // Hide nav immediately
              setNavClickedRecently(true); // Set flag to prevent auto-show
              setUserHasScrolledManually(false); // Reset manual scroll flag
              setSmoothScrolling(true); // Mark that we're smooth scrolling

              const el = document.getElementById(hash.slice(1));
              if (el) {
                el.scrollIntoView({ behavior: "smooth" });

                // Keep navbar hidden longer after navigation click
                setTimeout(() => {
                  setSmoothScrolling(false);
                  // Don't reset navClickedRecently here - let manual scroll handle it
                }, 1200);
              }
            }
          }
        }}
      >
        {!isHomePage && <Link to="/">Home</Link>}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleSectionNavigation("projects");
          }}
        >
          <a>Projects</a>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            handleSectionNavigation("about");
          }}
        >
          <a>About</a>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            handleSectionNavigation("testimonials");
          }}
        >
          <a>Testimonials</a>
        </button>
      </nav>
      <p
        className={`contact-btn${isAnimating ? " btn-animate" : ""}`}
        ref={contactRef}
        onClick={() => {
          setIsAnimating(true);
          setIsContactModalOpen(true);
          setTimeout(() => setIsAnimating(false), 300);
        }}
        style={{
          transform: `translate(${contactPos.x}px, ${contactPos.y}px)`,
          transition:
            contactPos.x === 0 && contactPos.y === 0
              ? "transform 0.4s cubic-bezier(0.2, 0.7, 0, 1)"
              : "none",
          boxShadow: `0 0 ${contactProximity * 28}px rgba(34, 197, 94, ${
            contactProximity * 0.8
          })`,
          borderColor:
            contactProximity > 0
              ? `rgba(34, 197, 94, ${Math.max(contactProximity * 0.8, 0.4)})`
              : "#868686",
        }}
      >
        Contact
      </p>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </header>
  );
}

export default Header;
